// Admin Portal: org model policy editor (2026-08-22). Disable a routable
// model for the whole org (the router drops it and every generation service
// 403s it) or weight its candidate budget (scales the router's pre-filled
// counts only). Weight 0 is blocked server-side like a disabled model (R4-4,
// 2026-10-07). Writes go through llm-proxy; see src/firebase/capabilities.js
// and src/ai/router/modelRouter.js.
import React, { useCallback, useEffect, useState } from 'react';
import { MODEL_IDS, normalizeModelPolicy, WEIGHT_MIN, WEIGHT_MAX } from '../../config/capabilities';
import { MODEL_REGISTRY } from '../../ai/registry/modelRegistry';
import { getOrg } from '../../firebase/orgs';
import { setOrgModelPolicy } from '../../firebase/capabilities';
import { useAuth } from '../../context/AuthContext';

const card = { background: '#0f1923', border: '1px solid #1e3a5f', borderRadius: 10, padding: '28px 32px', marginBottom: 28 };
const sectionHeader = { color: '#e2e8f0', fontWeight: 700, fontSize: '1rem', marginBottom: 20, letterSpacing: 0.5, textTransform: 'uppercase' };
const row = { display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0', borderBottom: '1px solid #0a1120' };
const saveBtn = { background: '#4ade8022', border: '1px solid #4ade80', borderRadius: 6, color: '#4ade80', fontWeight: 600, fontSize: 12, padding: '4px 10px', cursor: 'pointer' };

function modelName(id) {
  return (MODEL_REGISTRY.find((m) => m.id === id) || {}).name || id;
}

export default function ModelPolicySection({ orgId }) {
  const { refreshCapabilities } = useAuth() || {};
  const [policy, setPolicy] = useState(normalizeModelPolicy({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const org = await getOrg(orgId);
      setPolicy(normalizeModelPolicy(org && org.modelPolicy));
    } catch (e) {
      setError(e.message || 'Failed to load model policy');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const update = (id, patch) => setPolicy((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const save = async () => {
    setSaving(true); setError(''); setSaved('');
    try {
      await setOrgModelPolicy(orgId, policy);
      setSaved('Saved.');
      await load();
      if (typeof refreshCapabilities === 'function') refreshCapabilities();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={card}>
      <div style={sectionHeader}>Models</div>
      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>
        Uncheck a model to keep it out of every run in your organization. Weight scales the candidate counts the planner fills in for a model (1 = default); a count set by hand in a plan is not reweighted. A weight of 0 blocks the model, the same as unchecking it.
      </div>
      {loading ? <div style={{ color: '#64748b' }}>Loading model policy...</div> : MODEL_IDS.map((id) => (
        <div key={id} style={row} data-testid={`model-${id}`}>
          <input type="checkbox" checked={policy[id].enabled} aria-label={`${modelName(id)} enabled`} onChange={(e) => update(id, { enabled: e.target.checked })} />
          <span style={{ color: '#e2e8f0', fontSize: 13, minWidth: 140 }}>{modelName(id)}</span>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            Weight
            <input type="range" min={WEIGHT_MIN} max={WEIGHT_MAX} step={0.1} value={policy[id].weight} disabled={!policy[id].enabled}
              aria-label={`${modelName(id)} weight`} onChange={(e) => update(id, { weight: Number(e.target.value) })} />
            <span style={{ color: '#e2e8f0', fontVariantNumeric: 'tabular-nums', width: 32 }}>{policy[id].weight.toFixed(1)}</span>
          </label>
          {!policy[id].enabled && <span style={{ color: '#f87171', fontSize: 12 }}>Disabled for this organization</span>}
          {policy[id].enabled && policy[id].weight === 0 && <span style={{ color: '#f87171', fontSize: 12 }}>Blocked at weight 0</span>}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
        <button type="button" style={saveBtn} disabled={saving || loading} onClick={save}>Save model policy</button>
        {saved && <span style={{ color: '#4ade80', fontSize: 13 }}>{saved}</span>}
        {error && <span style={{ color: '#f87171', fontSize: 13 }}>{error}</span>}
      </div>
    </div>
  );
}
